import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import TanStackTable from '@spark/ui-components/components/TanStackTable/TanStackTable';
import TableBadge from '@spark/ui-components/components/TanStackTable/components/TableBadge';
import TruncatedText from '@spark/ui-components/components/TanStackTable/components/TruncatedText';
import { getJanelaDescargaAuditorias, getJanelasDescargaAuditoriasGeral } from '@spark/services/descarga';
import { notification } from '@spark/ui-components/utils/notification';
import { formatarDataComHora } from '../../shared/controleDescargaMapper';
import { paraRascunho, textoDestino } from '../utils/janelaDescarga';

const PAGE_SIZE = 10;

const ACOES = {
  CRIACAO: { label: 'Criação', variant: 'success' },
  ATUALIZACAO: { label: 'Alteração', variant: 'default' },
  EXCLUSAO: { label: 'Exclusão', variant: 'danger' }
};

const normalizarResposta = (response) => {
  const data = response?.data || response || {};
  const registros = Array.isArray(data.registros)
    ? data.registros
    : Array.isArray(data.Registros)
      ? data.Registros
      : [];

  return {
    totalRegistros: data.totalRegistros ?? data.TotalRegistros ?? registros.length,
    registros: registros.map((item, index) => ({
      id: item.id ?? item.Id ?? `${index}-${item.dataAcao ?? item.DataAcao ?? 'auditoria'}`,
      janelaId: item.janelaId ?? item.JanelaId ?? null,
      janelaNome: item.janelaNome ?? item.JanelaNome ?? '-',
      tipoAcao: item.tipoAcao ?? item.TipoAcao ?? '-',
      nomeColuna: item.nomeColuna ?? item.NomeColuna ?? '-',
      valorAntigo: item.valorAntigo ?? item.ValorAntigo ?? '-',
      valorNovo: item.valorNovo ?? item.ValorNovo ?? '-',
      usuario: item.usuario ?? item.Usuario ?? '-',
      dataAcao: item.dataAcao ?? item.DataAcao ?? null
    }))
  };
};

const renderTexto = (maxLength) => ({ getValue }) => (
  <TruncatedText value={getValue() || '-'} maxLength={maxLength} />
);

const getColumns = (geral) => [
  {
    id: 'dataAcao',
    accessorKey: 'dataAcao',
    header: 'Data da ação',
    size: 160,
    cell: ({ getValue }) => <span>{formatarDataComHora(getValue())}</span>
  },
  ...(geral
    ? [{
      id: 'janelaNome',
      accessorKey: 'janelaNome',
      header: 'Janela',
      size: 200,
      cell: renderTexto(26)
    }]
    : []),
  {
    id: 'tipoAcao',
    accessorKey: 'tipoAcao',
    header: 'Ação',
    size: 120,
    cell: ({ getValue }) => {
      const valor = getValue();
      const acao = ACOES[valor];
      return <TableBadge label={acao?.label ?? String(valor ?? '-')} variant={acao?.variant ?? 'default'} />;
    }
  },
  {
    id: 'nomeColuna',
    accessorKey: 'nomeColuna',
    header: 'Campo',
    size: 180,
    cell: renderTexto(22)
  },
  {
    id: 'valorAntigo',
    accessorKey: 'valorAntigo',
    header: 'Valor anterior',
    size: 220,
    cell: renderTexto(28)
  },
  {
    id: 'valorNovo',
    accessorKey: 'valorNovo',
    header: 'Valor novo',
    size: 220,
    cell: renderTexto(28)
  },
  {
    id: 'usuario',
    accessorKey: 'usuario',
    header: 'Usuário',
    size: 170,
    cell: renderTexto(22)
  }
];

const JanelaHistoricoModal = ({ show, janela = null, onHide }) => {
  const geral = !janela;
  const janelaId = janela?.id;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });

  const columns = useMemo(() => getColumns(geral), [geral]);

  const fetchAuditorias = useCallback(async () => {
    if (!show) return;

    const params = { PaginaAtual: pagination.pageIndex + 1, QuantidadePorPagina: pagination.pageSize };

    try {
      setLoading(true);
      const response = geral
        ? await getJanelasDescargaAuditoriasGeral(params)
        : await getJanelaDescargaAuditorias(janelaId, params);
      const auditorias = normalizarResposta(response);
      setData(auditorias.registros);
      setTotalRegistros(auditorias.totalRegistros);
    } catch (error) {
      console.error('Erro ao carregar histórico da janela de descarga:', error);
      notification.error('Não foi possível carregar o histórico.');
      setData([]);
      setTotalRegistros(0);
    } finally {
      setLoading(false);
    }
  }, [geral, janelaId, pagination.pageIndex, pagination.pageSize, show]);

  useEffect(() => {
    fetchAuditorias();
  }, [fetchAuditorias]);

  useEffect(() => {
    if (!show) return;
    setPagination({ pageIndex: 0, pageSize: PAGE_SIZE });
  }, [show, janelaId]);

  const handlePaginationChange = useCallback((next) => {
    setPagination((prev) => {
      if (typeof next === 'function') return next(prev);
      if (typeof next === 'number') return { ...prev, pageIndex: next };
      if (next && typeof next === 'object') return { ...prev, ...next };
      return prev;
    });
  }, []);

  const subtitulo = geral
    ? `${totalRegistros === 1 ? '1 registro' : `${totalRegistros} registros`} em todas as janelas`
    : `${janela.nome} · ${textoDestino(paraRascunho(janela))}`;

  return (
    <Modal show={show} onHide={onHide} animation={false} size="xl">
      <Modal.Header closeButton>
        <div>
          <Modal.Title>{geral ? 'Histórico geral de alterações' : 'Histórico de alterações'}</Modal.Title>
          <small className="text-muted">{subtitulo}</small>
        </div>
      </Modal.Header>
      <Modal.Body>
        {data.length === 0 && !loading ? (
          <div className="text-muted">
            {geral
              ? 'Nenhuma alteração registrada nas janelas.'
              : 'Nenhuma alteração registrada para esta janela.'}
          </div>
        ) : (
          <TanStackTable
            key={`janela-historico-${janelaId ?? 'geral'}-${pagination.pageIndex}-${pagination.pageSize}`}
            columns={columns}
            data={data}
            loading={loading}
            isSortable={false}
            withPagination={true}
            manualPagination={true}
            totalItems={totalRegistros}
            controlledPagination={pagination}
            onPaginationChange={handlePaginationChange}
            pageSizeOptions={[10, 25, 50, 100]}
            enableColumnConfig={true}
            columnConfigKey={geral ? 'descarga-janela-auditoria-geral-columns' : 'descarga-janela-auditoria-columns'}
            enableExportExcel={true}
            exportFileName="descarga-janela-historico"
            className="mt-3"
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default JanelaHistoricoModal;
